<ez-section>
  <ez-block>
    <div class="phms-intake-reconciliation">
      <div class="page-header">
        <div>
          <h2>{{ pageTitle }}</h2>
          <p class="page-subtitle">{{ pageSubtitle }}</p>
        </div>
      </div>

      <p class="loading-text" *ngIf="!canSubmit()" role="status" aria-live="polite">
        You do not have permission to submit intake submissions. Frontend gating is a UX hint - server authorization is authoritative.
      </p>

      <ng-container *ngIf="canSubmit()">
        <div class="message-box success" *ngIf="successMessage()" role="status" aria-live="polite">
          {{ successMessage() }}
        </div>

        <div class="message-box error" *ngIf="errorState() as error" role="alert" aria-live="assertive">
          <p>{{ error.title }}</p>
          <ul>
            <li *ngFor="let message of error.messages">{{ message }}</li>
          </ul>
          <div class="error-meta" *ngIf="error.problemCode || error.issueCodes.length > 0 || error.correlationId">
            <span *ngIf="error.problemCode">Problem code: {{ error.problemCode }}</span>
            <span *ngIf="error.issueCodes.length > 0">Issue codes: {{ error.issueCodes.join(', ') }}</span>
            <span *ngIf="error.correlationId">Correlation ID: {{ error.correlationId }}</span>
          </div>
        </div>

        <p class="loading-text" *ngIf="!programScopeKeysLoaded()" role="status" aria-live="polite">
          Loading program scopes...
        </p>

        <div class="upload-panel" *ngIf="programScopeKeysLoaded()">
          <label class="field">
            <span class="field-label">Source system key</span>
            <input
              class="field-input"
              type="text"
              placeholder="Governed source system key"
              [value]="sourceSystemKey()"
              [disabled]="isIngesting() || isLoadingRun()"
              (input)="onSourceSystemKeyChange($any($event.target).value)"
            />
          </label>

          <label class="field">
            <span class="field-label">{{ programScopeKeyLabel() }}</span>
            <select
              class="field-input"
              [value]="programScopeKey()"
              [disabled]="isIngesting() || !hasProgramScopeKeyOptions()"
              (change)="onProgramScopeKeyChange($any($event.target).value)"
            >
              <option value="">Select a program scope...</option>
              <option *ngFor="let option of programScopeKeyOptions()" [value]="option.value">
                {{ option.viewValue }}
              </option>
            </select>
            <span class="field-hint" *ngIf="!hasProgramScopeKeyOptions()">
              No program scopes are configured for intake.
            </span>
          </label>

          <label class="field">
            <span class="field-label">CSV file</span>
            <input
              class="field-input"
              type="file"
              accept=".csv,text/csv"
              [disabled]="isIngesting()"
              (change)="onFileSelected($event)"
            />
            <span class="field-hint">
              Expected headers: incarceratedPersonDisplayName, incarceratedPersonIdentifier, programScopeKey, sourceRecordKey (optional).
            </span>
          </label>

          <div class="form-actions">
            <ez-button
              [buttonText]="isIngesting() ? 'Ingesting...' : 'Ingest interface feed'"
              [disabled]="isIngesting()"
              (click)="ingest()"
            ></ez-button>
          </div>

          <div class="load-run">
            <label class="field">
              <span class="field-label">Load existing run by id</span>
              <input
                class="field-input"
                type="number"
                min="1"
                placeholder="Reconciliation run id"
                [value]="loadRunId()"
                [disabled]="isLoadingRun()"
                (input)="onLoadRunIdChange($any($event.target).value)"
              />
            </label>
            <div class="form-actions">
              <ez-button
                severity="secondary"
                [buttonText]="isLoadingRun() ? 'Loading...' : 'Load run'"
                [disabled]="isLoadingRun()"
                (click)="loadRun()"
              ></ez-button>
            </div>
          </div>
        </div>

        <ng-container *ngIf="hasRun() && run() as currentRun">
          <div class="run-summary">
            <h3>Reconciliation run #{{ currentRun.id }} - {{ currentRun.sourceSystemKey }}</h3>
            <div class="counts">
              <span>Status: {{ currentRun.runStatusCode }}</span>
              <span class="count-inserted">Inserted: {{ currentRun.insertedCount }}</span>
              <span class="count-matched">Matched: {{ currentRun.matchedCount }}</span>
              <span class="count-conflict">Conflicts: {{ currentRun.conflictCount }}</span>
              <span
                class="count-attention"
                [class.count-attention--active]="currentRun.attentionRequiredCount > 0"
                data-testid="attention-count"
              >Needs review: {{ currentRun.attentionRequiredCount }}</span>
            </div>
            <div class="counts">
              <span>Started: {{ currentRun.startedAtUtc }}</span>
              <span *ngIf="currentRun.completedAtUtc">Completed: {{ currentRun.completedAtUtc }}</span>
            </div>
          </div>

          <table class="reconciliation-table">
            <thead>
              <tr>
                <th scope="col">Source record key</th>
                <th scope="col">Result</th>
                <th scope="col">Attention</th>
                <th scope="col">Matched submission</th>
                <th scope="col">Conflict detail</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let result of currentRun.results"
                [class.row-conflict]="result.resultStatusCode === 'conflict'"
                [class.row-attention]="result.attentionStateCode === 'needs_review'"
              >
                <td>{{ result.sourceRecordKey }}</td>
                <td>{{ result.resultStatusCode }}</td>
                <td>
                  <span
                    class="attention-state"
                    [class.attention-state--active]="result.attentionStateCode === 'needs_review'"
                  >{{ attentionStateLabel(result.attentionStateCode) }}</span>
                </td>
                <td>{{ result.matchedSubmissionId ?? '—' }}</td>
                <td>
                  <span *ngIf="result.conflictDetail.length > 0; else noConflict">
                    {{ result.conflictDetail.join('; ') }}
                  </span>
                  <ng-template #noConflict>
                    <span class="conflict-ok">&mdash;</span>
                  </ng-template>
                </td>
              </tr>
            </tbody>
          </table>
        </ng-container>
      </ng-container>
    </div>
  </ez-block>
</ez-section>
